import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext.js';
import { DirtyFormProvider } from './contexts/DirtyFormContext.js';

// 1. 레이아웃 및 인증
import Layout from './components/Layout.js';
import LoginPage from './pages/LoginPage.js';
import RegisterPage from './pages/RegisterPage.js'; 

// 2. 페이지 임포트
import DashboardPage from './pages/DashboardPage.js';
import ParticipantRegisterPage from './pages/ParticipantRegisterPage.js'; 
import ParticipantManagePage from './pages/ParticipantManagePage.js';
import WorkManagePage from './pages/WorkManagePage.js';
import ScheduleManagePage from './pages/ScheduleManagePage.js';
import LeaveManagePage from './pages/LeaveManagePage.js';
import ProjectSettingsPage from './pages/ProjectSettingsPage.js';
import PersonalSettingsPage from './pages/PersonalSettingsPage.js';
import AdminSettingsPage from './pages/AdminSettingsPage.js';
import AdminDataCleanupPage from './pages/AdminDataCleanupPage.js';
import AdminBackupPage from './pages/AdminBackupPage.js';
import BudgetPage from './pages/BudgetPage.js';
import LaborBudgetPage from './pages/LaborBudgetPage.js';
import WeeklyAllowancePage from './pages/WeeklyAllowancePage.js'; 

// ⬇️ [신규] "연차 정산" 페이지 임포트 (실제 파일 경로로 수정)
import LeaveSettleIndividualPage from './pages/LeaveSettleIndividualPage.js';
import LeaveSettleBatchPage from './pages/LeaveSettleBatchPage.js';
import LeaveSettlePendingPage from './pages/LeaveSettlePendingPage.js';
import SettleCancelPage from './pages/SettleCancelPage.js';

// ⬇️ [신규] "연차 사용등록" 및 "연차승인" 페이지 임포트
import LeaveRegisterPage from './pages/LeaveRegisterPage.js';
import LeaveApplicationInquiryPage from './pages/LeaveApplicationInquiryPage.js';
import LeaveApprovalPage from './pages/LeaveApprovalPage.js';
import LeaveVerifyPage from './pages/LeaveVerifyPage.js';
import LeaveVerificationHistoryPage from './pages/LeaveVerificationHistoryPage.js';
import PayrollRegisterPage from './pages/PayrollRegisterPage.js';
import PayrollAutoTransferPage from './pages/PayrollAutoTransferPage.js';
import PayrollSendPage from './pages/PayrollSendPage.js';
import PayrollSettingsPage from './pages/PayrollSettingsPage.js';
import PayrollEmailSettingsPage from './pages/PayrollEmailSettingsPage.js';
import MailboxPage from './pages/MailboxPage.js';
import DisabledWorkerRosterPage from './pages/DisabledWorkerRosterPage.js';
import ExcludedWorkerListPage from './pages/ExcludedWorkerListPage.js';
import EmploymentInsurancePage from './pages/EmploymentInsurancePage.js';
import SocialInsuranceExportPage from './pages/SocialInsuranceExportPage.js';
import CopyrightPage from './pages/CopyrightPage.js';


// ( ... ProtectedRoute, AdminRoute ... - 변경 없음)
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

const AdminRoute = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (user?.role !== 'admin') {
    return <Navigate to="/" replace />; 
  }
  return children;
};


function App() {
  return (
    <Routes>
      {/* 1. 로그인/가입 페이지 (레이아웃 없음) */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} /> 

      {/* 2. 로그인 후 페이지 (레이아웃 적용) */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DirtyFormProvider>
              <Layout />
            </DirtyFormProvider>
          </ProtectedRoute>
        }
      >
        {/* Layout의 <Outlet /> 으로 렌더링될 자식 페이지들 */}
        
        <Route index element={<DashboardPage />} />
        
        <Route path="participant-register" element={<ParticipantRegisterPage />} /> 
        <Route path="manage" element={<ParticipantManagePage />} /> 
        <Route path="work" element={<WorkManagePage />} />
        <Route path="schedule" element={<ScheduleManagePage />} />
        <Route path="leave" element={<LeaveManagePage />} /> 

        {/* --- [신규] 연차 정산 라우트 (변경 없음) --- */}
        <Route path="leave-settle/individual" element={<LeaveSettleIndividualPage />} />
        <Route path="leave-settle/batch" element={<LeaveSettleBatchPage />} />
        <Route path="leave-settle/pending" element={<LeaveSettlePendingPage />} />
        <Route path="leave-settle/cancel" element={<SettleCancelPage />} />
        
        {/* --- [신규] 연차 관리 하위 메뉴 라우트 --- */}
        <Route path="leave-register" element={<LeaveRegisterPage />} />
        <Route path="leave-inquiry" element={<LeaveApplicationInquiryPage />} />
        <Route path="leave-verify/history" element={<LeaveVerificationHistoryPage />} />
        <Route path="leave-verify" element={<LeaveVerifyPage />} />
        <Route
          path="leave-approval"
          element={
            <AdminRoute>
              <LeaveApprovalPage />
            </AdminRoute>
          }
        />
        
        <Route path="budget" element={<BudgetPage />} />
        <Route path="labor-budget" element={<LaborBudgetPage />} />
        <Route path="weekly-allowance" element={<WeeklyAllowancePage />} />
        <Route path="payroll/register" element={<PayrollRegisterPage />} />
        <Route path="payroll/auto-transfer" element={<PayrollAutoTransferPage />} />
        <Route path="payroll/send" element={<PayrollSendPage />} />
        <Route
          path="payroll/settings"
          element={
            <AdminRoute>
              <PayrollSettingsPage />
            </AdminRoute>
          }
        />
        <Route
          path="payroll/email-settings"
          element={
            <AdminRoute>
              <PayrollEmailSettingsPage />
            </AdminRoute>
          }
        />
        <Route path="mailbox" element={<MailboxPage />} />
        <Route
          path="insurance/employment"
          element={
            <AdminRoute>
              <EmploymentInsurancePage />
            </AdminRoute>
          }
        />
        <Route path="insurance/export" element={<SocialInsuranceExportPage />} />
        <Route path="disabled-worker-roster" element={<DisabledWorkerRosterPage />} />
        <Route path="excluded-workers" element={<ExcludedWorkerListPage />} />
        
        <Route path="project-settings" element={<ProjectSettingsPage />} /> 
        <Route path="personal-settings" element={<PersonalSettingsPage />} /> 
        <Route path="copyright" element={<CopyrightPage />} />

        <Route
          path="admin-settings"
          element={
            <AdminRoute>
              <AdminSettingsPage />
            </AdminRoute>
          }
        />
        <Route
          path="admin-data-cleanup"
          element={
            <AdminRoute>
              <AdminDataCleanupPage />
            </AdminRoute>
          }
        />
        <Route
          path="admin-backup"
          element={
            <AdminRoute>
              <AdminBackupPage />
            </AdminRoute>
          }
        />
      </Route>

      {/* 3. 그 외 모든 잘못된 경로는 메인으로 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;